/**
 * Training vocabulary shared by the backend, the training engine and every app.
 * See docs/research/gravl-mechanics.md for how each value is used.
 */

/** Who a training or diet plan belongs to (ADR 0004). Derived from which owner columns are set. */
export enum PlanOwnerKind {
  CLIENT = "client",
  ATHLETE = "athlete",
  TEMPLATE = "template",
}

/** A business template is a whole plan, or a workout template: one training to drop into a coached one. */
export enum TrainingPlanKind {
  PLAN = "plan",
  WORKOUT = "workout",
}

/** Fixed plans are written by hand; adaptive plans are generated by the training engine. */
export enum TrainingPlanMode {
  FIXED = "fixed",
  ADAPTIVE = "adaptive",
}

/**
 * Muscles the engine tracks recovery and volume for. Finer than a body part on purpose: quads and
 * hamstrings recover and get trained on different days, and so do the three heads of the delts.
 */
export enum Muscle {
  CHEST = "chest",
  UPPER_BACK = "upper_back",
  LATS = "lats",
  LOWER_BACK = "lower_back",
  TRAPS = "traps",
  FRONT_DELTS = "front_delts",
  SIDE_DELTS = "side_delts",
  REAR_DELTS = "rear_delts",
  BICEPS = "biceps",
  TRICEPS = "triceps",
  FOREARMS = "forearms",
  ABS = "abs",
  OBLIQUES = "obliques",
  GLUTES = "glutes",
  QUADS = "quads",
  HAMSTRINGS = "hamstrings",
  ADDUCTORS = "adductors",
  ABDUCTORS = "abductors",
  CALVES = "calves",
  NECK = "neck",
}

/** Large muscles recover more slowly and anchor a recovery-split day. */
export const LARGE_MUSCLES: readonly Muscle[] = [
  Muscle.CHEST,
  Muscle.UPPER_BACK,
  Muscle.LATS,
  Muscle.QUADS,
  Muscle.HAMSTRINGS,
  Muscle.GLUTES,
];

export enum EquipmentCategory {
  FREE_WEIGHTS = "free_weights",
  BARS_AND_PLATES = "bars_and_plates",
  BENCHES_AND_RACKS = "benches_and_racks",
  CABLES = "cables",
  PLATED_MACHINES = "plated_machines",
  WEIGHT_MACHINES = "weight_machines",
  BANDS_AND_ACCESSORIES = "bands_and_accessories",
  CARDIO = "cardio",
}

/** How an equipment item's loads are described, which decides how a target weight snaps to it. */
export enum EquipmentLoadKind {
  /** No load of its own (a bench, a pull-up bar). */
  NONE = "none",
  /** A fixed list of weights: the dumbbell or kettlebell rack. */
  FIXED_WEIGHTS = "fixed_weights",
  /** A bar loaded with plates in pairs. */
  PLATES = "plates",
  /** A selectorised stack: min, max and step. */
  STACK = "stack",
}

/**
 * The equipment catalogue. Keys are stable identifiers stored on gyms and exercises; never rename
 * one once shipped. Custom machines a gym adds get their own key with the `custom:` prefix.
 */
export const EQUIPMENT = {
  dumbbells: { category: EquipmentCategory.FREE_WEIGHTS, load: EquipmentLoadKind.FIXED_WEIGHTS },
  kettlebells: { category: EquipmentCategory.FREE_WEIGHTS, load: EquipmentLoadKind.FIXED_WEIGHTS },
  barbell: { category: EquipmentCategory.BARS_AND_PLATES, load: EquipmentLoadKind.PLATES },
  ez_bar: { category: EquipmentCategory.BARS_AND_PLATES, load: EquipmentLoadKind.PLATES },
  trap_bar: { category: EquipmentCategory.BARS_AND_PLATES, load: EquipmentLoadKind.PLATES },
  plates: { category: EquipmentCategory.BARS_AND_PLATES, load: EquipmentLoadKind.NONE },
  flat_bench: { category: EquipmentCategory.BENCHES_AND_RACKS, load: EquipmentLoadKind.NONE },
  adjustable_bench: { category: EquipmentCategory.BENCHES_AND_RACKS, load: EquipmentLoadKind.NONE },
  squat_rack: { category: EquipmentCategory.BENCHES_AND_RACKS, load: EquipmentLoadKind.NONE },
  smith_machine: { category: EquipmentCategory.BENCHES_AND_RACKS, load: EquipmentLoadKind.PLATES },
  pull_up_bar: { category: EquipmentCategory.BENCHES_AND_RACKS, load: EquipmentLoadKind.NONE },
  dip_station: { category: EquipmentCategory.BENCHES_AND_RACKS, load: EquipmentLoadKind.NONE },
  preacher_bench: { category: EquipmentCategory.BENCHES_AND_RACKS, load: EquipmentLoadKind.NONE },
  cable_station: { category: EquipmentCategory.CABLES, load: EquipmentLoadKind.STACK },
  lat_pulldown: { category: EquipmentCategory.CABLES, load: EquipmentLoadKind.STACK },
  seated_row: { category: EquipmentCategory.CABLES, load: EquipmentLoadKind.STACK },
  leg_press: { category: EquipmentCategory.PLATED_MACHINES, load: EquipmentLoadKind.PLATES },
  hack_squat: { category: EquipmentCategory.PLATED_MACHINES, load: EquipmentLoadKind.PLATES },
  chest_press_machine: { category: EquipmentCategory.WEIGHT_MACHINES, load: EquipmentLoadKind.STACK },
  shoulder_press_machine: {
    category: EquipmentCategory.WEIGHT_MACHINES,
    load: EquipmentLoadKind.STACK,
  },
  pec_deck: { category: EquipmentCategory.WEIGHT_MACHINES, load: EquipmentLoadKind.STACK },
  leg_extension: { category: EquipmentCategory.WEIGHT_MACHINES, load: EquipmentLoadKind.STACK },
  leg_curl: { category: EquipmentCategory.WEIGHT_MACHINES, load: EquipmentLoadKind.STACK },
  hip_thrust_machine: { category: EquipmentCategory.WEIGHT_MACHINES, load: EquipmentLoadKind.STACK },
  calf_raise_machine: { category: EquipmentCategory.WEIGHT_MACHINES, load: EquipmentLoadKind.STACK },
  adductor_machine: { category: EquipmentCategory.WEIGHT_MACHINES, load: EquipmentLoadKind.STACK },
  abductor_machine: { category: EquipmentCategory.WEIGHT_MACHINES, load: EquipmentLoadKind.STACK },
  assisted_pull_up: { category: EquipmentCategory.WEIGHT_MACHINES, load: EquipmentLoadKind.STACK },
  resistance_bands: { category: EquipmentCategory.BANDS_AND_ACCESSORIES, load: EquipmentLoadKind.NONE },
  ab_wheel: { category: EquipmentCategory.BANDS_AND_ACCESSORIES, load: EquipmentLoadKind.NONE },
  medicine_ball: { category: EquipmentCategory.BANDS_AND_ACCESSORIES, load: EquipmentLoadKind.FIXED_WEIGHTS },
  exercise_ball: { category: EquipmentCategory.BANDS_AND_ACCESSORIES, load: EquipmentLoadKind.NONE },
  treadmill: { category: EquipmentCategory.CARDIO, load: EquipmentLoadKind.NONE },
  stationary_bike: { category: EquipmentCategory.CARDIO, load: EquipmentLoadKind.NONE },
  rowing_machine: { category: EquipmentCategory.CARDIO, load: EquipmentLoadKind.NONE },
  elliptical: { category: EquipmentCategory.CARDIO, load: EquipmentLoadKind.NONE },
  stair_climber: { category: EquipmentCategory.CARDIO, load: EquipmentLoadKind.NONE },
} as const satisfies Record<string, { category: EquipmentCategory; load: EquipmentLoadKind }>;

export type EquipmentKey = keyof typeof EQUIPMENT;

export const EQUIPMENT_KEYS = Object.keys(EQUIPMENT) as EquipmentKey[];

/** Prefix of a gym's own machine, e.g. `custom:3f2a…`. */
export const CUSTOM_EQUIPMENT_PREFIX = "custom:";

/** Large, small and garage gyms start from different equipment presets. */
export enum GymKind {
  LARGE = "large",
  SMALL = "small",
  GARAGE = "garage",
}

export enum WeightUnit {
  KG = "kg",
  LB = "lb",
}

export enum TrainingGoal {
  STRONGER = "stronger",
  MUSCLE = "muscle",
  LEAN = "lean",
  GENERAL_FITNESS = "general_fitness",
}

export enum TrainingExperience {
  NONE = "none",
  BEGINNER = "beginner",
  INTERMEDIATE = "intermediate",
  ADVANCED = "advanced",
}

/** How much the exercise selection changes from one workout to the next. */
export enum TrainingVariety {
  CONSISTENT = "consistent",
  BALANCED = "balanced",
  VARIED = "varied",
}

export enum RepScheme {
  /** Hardest set first, then one rep fewer each set: 8-7-6. */
  STRAIGHT = "straight",
  /** Weight climbs to a heavy top set. */
  PYRAMID = "pyramid",
  /** The top set first, then lighter sets. */
  REVERSE_PYRAMID = "reverse_pyramid",
}

export enum TrainingSplit {
  /** Trains the muscles that are recovered and were trained least recently. */
  RECOVERY = "recovery",
  FULL_BODY = "full_body",
  UPPER_LOWER = "upper_lower",
  PUSH_PULL_LEGS = "push_pull_legs",
}

export enum ExerciseMechanic {
  COMPOUND = "compound",
  ISOLATION = "isolation",
}

/** What one rep of an exercise is measured in. */
export enum ExerciseMeasure {
  REPS = "reps",
  TIME = "time",
  DISTANCE = "distance",
}

/** How hard an exercise felt: reps left in reserve on the hardest set. Unrated counts as hard. */
export enum EffortRating {
  /** 3 or more reps in reserve. */
  EASY = "easy",
  /** 1–2 reps in reserve. */
  IDEAL = "ideal",
  /** No reps in reserve. */
  HARD = "hard",
}

export enum WorkoutSetType {
  WARMUP = "warmup",
  WORKING = "working",
  DROP = "drop",
}

export enum WorkoutStatus {
  PLANNED = "planned",
  IN_PROGRESS = "in_progress",
  COMPLETED = "completed",
  DISCARDED = "discarded",
}

/** What a logged cardio session was; it decides which muscles it tires. */
export enum CardioKind {
  RUN = "run",
  RIDE = "ride",
  WALK = "walk",
  ROW = "row",
  SWIM = "swim",
  OTHER = "other",
}

/** How the athlete feels before a workout. Tired makes it a light day. */
export enum Readiness {
  TIRED = "tired",
  NORMAL = "normal",
  GREAT = "great",
}

export enum DeloadIntensity {
  LIGHT = "light",
  MODERATE = "moderate",
}

export enum Sex {
  MALE = "male",
  FEMALE = "female",
}

/**
 * The lifts an athlete can tell us a known set for ("80 kg × 5 on the bench"). One per region the
 * engine calibrates: pushing, the two big leg lifts, pulling.
 */
export enum KeyLift {
  BENCH_PRESS = "bench_press",
  SQUAT = "squat",
  DEADLIFT = "deadlift",
  LAT_PULLDOWN = "lat_pulldown",
}

/** True for a catalogue key, false for a gym's own `custom:` machine. */
export const isEquipmentKey = (key: string): key is EquipmentKey => Object.hasOwn(EQUIPMENT, key);
